import React, { useState, useCallback, useMemo } from "react";
import {
  useDocumentData,
  useCollectionData,
} from "react-firebase-hooks/firestore";
import UploadModal from "./UploadModal";
import AdminFeaturedList from "./AdminFeaturedList";
import RegularFeaturedList from "./RegularFeaturedList";
import Button from "./Button";
import Input from "./Input";
import Tooltip from "./Tooltip";
import Roles from "../constants/roles";
import { firestore } from "../firebase/firebase";
import firebase from "firebase/app";
import { initializeFeaturedOrder } from "../utils/initializeFeaturedOrder";
import { searchLatvianText } from "../utils/normalizeLatvian";
import DbUser from "../interfaces/dbuser";
import SheetMusic from "../interfaces/sheetmusic";

interface CombinedMusicListProps {
  user: firebase.User;
  onAdminStatusChange?: (isAdmin: boolean, onUploadClick: () => void) => void;
}

const CombinedMusicList = ({ user, onAdminStatusChange }: CombinedMusicListProps) => {
  const userRef = firestore.doc(`users/${user?.uid}`);
  const [dbUser] = useDocumentData<DbUser>(userRef);

  const musicRef = firestore.collection("music");
  const [music] = useCollectionData<SheetMusic>(
    musicRef.orderBy("title")
  );

  const [upload, setupload] = useState(false);
  const [filter, setfilter] = useState("");
  const [localFeaturedMusic, setLocalFeaturedMusic] = useState<SheetMusic[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean, sheet: SheetMusic | null}>({show: false, sheet: null});
  const [editSheet, setEditSheet] = useState<SheetMusic | null>(null);

  const isAdmin = dbUser?.roles.includes(Roles.admin) || false;

  // Create stable upload handler function
  const handleUpload = useCallback(() => {
    setupload((prev) => !prev);
  }, []);

  // Notify parent component about admin status and upload handler
  React.useEffect(() => {
    if (onAdminStatusChange && dbUser) {
      onAdminStatusChange(isAdmin, handleUpload);
    }
  }, [isAdmin, onAdminStatusChange, dbUser, handleUpload]);

  const onFeatured = async (sheet: SheetMusic) => {
    console.log(sheet);

    let ref = musicRef.doc(sheet.id);
    
    if (sheet.featured) {
      // Remove from featured
      await ref.update({
        featured: false,
        featuredOrder: null,
      });
    } else {
      // Add to featured - assign next order number
      const currentFeaturedCount = localFeaturedMusic.length;
      await ref.update({
        featured: true,
        featuredOrder: currentFeaturedCount,
      });
    }
  };

  const featuredMusic = useMemo(() => {
    if (!music) return [];
    return music
      .filter(sheet => sheet.featured)
      .sort((a, b) => {
        const orderA = a.featuredOrder !== undefined && a.featuredOrder !== null ? a.featuredOrder : 999;
        const orderB = b.featuredOrder !== undefined && b.featuredOrder !== null ? b.featuredOrder : 999;
        return orderA - orderB;
      });
  }, [music]);

  // Update local featured music when data changes (but not during drag operations)
  React.useEffect(() => {
    if (!isDragging) {
      setLocalFeaturedMusic(featuredMusic);
    }
  }, [featuredMusic, isDragging]);

  // Initialize featured order for existing items (run once)
  React.useEffect(() => {
    if (isAdmin && music && music.length > 0) {
      initializeFeaturedOrder();
    }
  }, [isAdmin, music?.length]);

  const handleFeaturedReorder = (newOrder: SheetMusic[]) => {
    setLocalFeaturedMusic(newOrder);
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    // Add a slight delay to ensure database updates complete before allowing Firebase updates
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  const handleAddDivider = async (title: string) => {
    try {
      const currentFeaturedCount = localFeaturedMusic.length;
      const musicRef = firestore.collection("music").doc();
      
      const divider: SheetMusic = {
        id: musicRef.id,
        title: title,
        author: '',
        downloadURL: '',
        name: '',
        size: 0,
        featured: true,
        featuredOrder: currentFeaturedCount,
        isDivider: true,
        added_on: firebase.firestore.Timestamp.fromDate(new Date()),
      };

      await musicRef.set(divider);
      console.log('Divider added successfully');
    } catch (error) {
      console.error('Error adding divider:', error);
    }
  };

  const handleDeleteSheet = async (sheet: SheetMusic) => {
    try {
      await musicRef.doc(sheet.id).delete();
      console.log('Sheet deleted successfully');
    } catch (error) {
      console.error('Error deleting sheet:', error);
    }
  };

  const showDeleteConfirmation = (sheet: SheetMusic) => {
    setDeleteConfirm({show: true, sheet});
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.sheet) {
      await handleDeleteSheet(deleteConfirm.sheet);
      setDeleteConfirm({show: false, sheet: null});
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm({show: false, sheet: null});
  };

  const handleEditSheet = (sheet: SheetMusic) => {
    setEditSheet(sheet);
  };

  const handleCloseEdit = () => {
    setEditSheet(null);
  };

  if (!music) {
    return null;
  }
  const allMusic = music.filter(sheet => {
    // Don't show dividers in all sheets section
    if (sheet.isDivider) {
      return false;
    }
    
    if (filter) {
      const searchableText = sheet.title + " " + sheet.author;
      return searchLatvianText(searchableText, filter);
    }
    return true;
  }).sort((a, b) => {
    // Use Latvian locale for proper sorting of Latvian characters
    return a.title.localeCompare(b.title, 'lv');
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Search Section - Always at top */}
      <div className="mb-8">
        <div className="relative">
          <Input
            label="Meklēt notis"
            type="text"
            value={filter}
            onChange={(value) => setfilter(value)}
            placeholder="Meklēt notis..."
          />
          {filter && (
            <button
              onClick={() => setfilter("")}
              className="absolute right-4 top-[30px] transform -translate-y-1/2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-full p-1 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Featured Music Section - Only show when no search */}
      {!filter && localFeaturedMusic.length > 0 && (
        <div className="mb-16">
          <h1 className="text-3xl font-bold text-zinc-800 mb-8 mt-8 ">
            Aktuālās notis
            {isAdmin && (
              <span className="ml-3 text-sm font-normal text-zinc-500">
                (Velciet aiz punktiņiem lai pārkārtotu)
              </span>
            )}
          </h1>
          {isAdmin ? (
            <AdminFeaturedList 
              featuredMusic={localFeaturedMusic} 
              onReorder={handleFeaturedReorder}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onAddDivider={handleAddDivider}
            />
          ) : (
            <RegularFeaturedList featuredMusic={localFeaturedMusic} />
          )}
        </div>
      )}

      {/* Admin Upload Section - Now moved to navbar */}

      {/* Upload Modal */}
      <UploadModal 
        isOpen={upload} 
        onClose={() => setupload(false)} 
      />

      {/* Edit Modal */}
      <UploadModal 
        isOpen={editSheet !== null} 
        onClose={handleCloseEdit}
        editMode={true}
        editSheet={editSheet || undefined}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && deleteConfirm.sheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-zinc-800 mb-4 ">
              Vai tiešām vēlies izdzēst šīs notis pavisam?
            </h3>
            <p className="text-zinc-600 mb-6 ">
              <strong>{deleteConfirm.sheet.title}</strong>, <span className="text-zinc-600">{deleteConfirm.sheet.author}</span>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-zinc-700 bg-zinc-200 rounded-lg hover:bg-zinc-300 transition-colors  font-medium"
              >
                Atcelt
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors  font-medium"
              >
                Izdzēst
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Music Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-zinc-800  mb-6 mt-16">
          {filter ? "Meklēšanas rezultāti" : "Visas notis"}
        </h2>
        <div className="space-y-2">
          {allMusic.map((sheet) => {
            if (isAdmin) {
              return (
                <div
                  key={sheet.id}
                  className="flex items-center justify-between p-4 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 hover:border-zinc-300 transition-colors duration-200 cursor-pointer min-h-[64px]"
                  onClick={() => window.open(sheet.downloadURL, '_blank', 'noopener,noreferrer')}
                >
                  <span className="text-zinc-800  font-medium">
                    <strong>{sheet.title}</strong>, <span className="text-zinc-500">{sheet.author}</span>
                  </span>
                  <div className="flex gap-3 justify-end">
                    {!sheet.featured && (
                      <Tooltip content="Pievienot aktuālajām" position="top">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onFeatured(sheet);
                          }}
                          className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-zinc-300 bg-transparent hover:bg-zinc-100 text-zinc-700 transition-colors"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip content="Rediģēt" position="top">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditSheet(sheet);
                        }}
                        className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-zinc-300 bg-transparent hover:bg-zinc-100 text-zinc-700 transition-colors"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </Tooltip>
                    <Tooltip content="Dzēst" position="top">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          showDeleteConfirmation(sheet);
                        }}
                        className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-red-300 bg-transparent hover:bg-red-50 text-red-600 transition-colors"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </Tooltip>
                  </div>
                </div>
              );
            }

            return (
              <a
                key={sheet.id}
                href={sheet.downloadURL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center w-full p-4 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 hover:border-zinc-300 transition-colors duration-200 text-left no-underline min-h-[64px]"
              >
                <span className="text-zinc-900  font-medium">
                  <strong>{sheet.title}</strong>, <span className="text-zinc-600">{sheet.author}</span>
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CombinedMusicList;